const SCROLL_DEDUPE_WINDOW = 500
const recentScrollMap = new Map()

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined'

const scrollableOverflowValues = ['auto', 'scroll', 'overlay']

function isScrollableElement(element) {
  if (!isBrowser() || !element || typeof window.getComputedStyle !== 'function') {
    return false
  }

  const { overflowY } = window.getComputedStyle(element)
  return scrollableOverflowValues.includes(overflowY) && element.scrollHeight > element.clientHeight
}

function findScrollContainer(target, rootElement) {
  if (!isBrowser() || !target) {
    return null
  }

  let current = target.parentElement
  while (current && current !== rootElement && current !== document.body) {
    if (isScrollableElement(current)) {
      return current
    }
    current = current.parentElement
  }

  if (isScrollableElement(rootElement)) {
    return rootElement
  }

  return null
}

function isVisibleInViewport(target) {
  if (!isBrowser() || !target || typeof target.getBoundingClientRect !== 'function') {
    return true
  }

  const targetRect = target.getBoundingClientRect()
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  return targetRect.top >= 0 && targetRect.bottom <= viewportHeight
}

function isVisibleInContainer(target, scrollContainer) {
  if (
    !isBrowser()
    || !target
    || !scrollContainer
    || typeof target.getBoundingClientRect !== 'function'
    || typeof scrollContainer.getBoundingClientRect !== 'function'
  ) {
    return true
  }

  const targetRect = target.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  return targetRect.top >= containerRect.top && targetRect.bottom <= containerRect.bottom
}

function cleanupRecentScroll(now) {
  recentScrollMap.forEach((timestamp, key) => {
    if (now - timestamp > SCROLL_DEDUPE_WINDOW) {
      recentScrollMap.delete(key)
    }
  })
}

function buildDedupeToken({ dedupeKey, requestKey }) {
  return `${dedupeKey || '__default__'}::${requestKey || '__same__'}`
}

function scrollWithinContainer(target, scrollContainer, behavior, block) {
  if (!scrollContainer || typeof scrollContainer.scrollTo !== 'function') {
    return false
  }

  const targetRect = target.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  let alignOffset = 0

  if (block === 'center') {
    alignOffset = (scrollContainer.clientHeight - targetRect.height) / 2
  } else if (block === 'end') {
    alignOffset = scrollContainer.clientHeight - targetRect.height
  }

  const top = scrollContainer.scrollTop + (targetRect.top - containerRect.top) - alignOffset
  scrollContainer.scrollTo({
    top: Math.max(top, 0),
    behavior,
  })

  return true
}

export function requestScrollIntoView({
  target,
  rootElement,
  dedupeKey,
  requestKey,
  behavior = 'smooth',
  block = 'start',
} = {}) {
  if (!isBrowser() || !target) {
    return false
  }

  const scrollContainer = findScrollContainer(target, rootElement)
  const containerInViewport = scrollContainer ? isVisibleInViewport(scrollContainer) : false
  const targetInContainer = scrollContainer ? isVisibleInContainer(target, scrollContainer) : false
  const targetInViewport = isVisibleInViewport(target)

  if (!scrollContainer && targetInViewport) {
    return false
  }

  if (scrollContainer && containerInViewport && targetInContainer) {
    return false
  }

  const now = Date.now()
  cleanupRecentScroll(now)

  const dedupeToken = buildDedupeToken({ dedupeKey, requestKey })
  const previousScrollTime = recentScrollMap.get(dedupeToken)
  if (typeof previousScrollTime === 'number' && now - previousScrollTime < SCROLL_DEDUPE_WINDOW) {
    return false
  }

  recentScrollMap.set(dedupeToken, now)

  if (scrollContainer && containerInViewport && !targetInContainer) {
    return scrollWithinContainer(target, scrollContainer, behavior, block)
  }

  if (typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ behavior, block })
    return true
  }

  return false
}

export function resetScrollDedupe() {
  recentScrollMap.clear()
}
